import api from './api';
import {
  filterPublicCourses,
  getCachedCourseDetail,
  invalidatePublicCourseCache,
  rememberPublicCourseList,
  setCachedCourseDetail,
  setCachedPublicCourseList,
} from './courseCatalogCache';
import { eventCatalogCache, filterPublicEvents } from './eventCatalogCache';
import {
  refreshMyEnrollments,
  rememberMyEnrollments,
  upsertMyEnrollment,
} from './enrollmentCache';
import { filterPublicProjects, projectCatalogCache } from './projectCatalogCache';
import {
  CourseDetail,
  CourseListItem,
  AuthorCard,
  Category,
  CategoryMenu,
  ContentDashboard,
  LabDetail,
  CourseEnrollment,
  CourseInvite,
  CourseAdminGrant,
  CourseInterviewPackageConfig,
  CatalogSettings,
  CourseProgress,
  EnrollmentReportRow,
  ProjectEnrollmentReportRow,
  LabListItem,
  KnowledgeModule,
  LabTopic,
  GeneratedContent,
  GeneratedCoursePlan,
  ProjectDetail,
  ProjectListItem,
  GigDetail,
  GigListItem,
  EventDetail,
  EventListItem,
  EventEnrollment,
  ProjectEnrollment,
  ProjectMentorRequest,
  GigEnrollment,
  NavMenuItem,
  KnowledgeTopic,
  CatalogTreeCategory,
  CatalogTreeLab,
  ModuleAssessment,
} from '../types/content';

interface Envelope<T> {
  success: boolean;
  message?: string | null;
  [key: string]: unknown;
}

type CatalogListData = {
  courses: CourseListItem[];
  totalCount: number;
  pageNumber?: number;
  pageSize?: number;
  success: boolean;
};

type CatalogItemData = {
  course?: CourseDetail;
  success: boolean;
};

type LabListData = {
  labs: LabListItem[];
  totalCount?: number;
  success: boolean;
};

type LabItemData = {
  lab?: LabDetail;
  success: boolean;
};

const asRecord = (data: unknown) => (data && typeof data === 'object' ? data as Record<string, unknown> : {});

const pickList = <T>(data: unknown, keys: string[]): T[] => {
  const record = asRecord(data);
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value as T[];
  }
  return [];
};

const pickOne = <T>(data: unknown, keys: string[]): T | undefined => {
  const record = asRecord(data);
  for (const key of keys) {
    const value = record[key];
    if (value) return value as T;
  }
  return undefined;
};

const normalizeCatalogList = (data: unknown): CatalogListData => {
  const items = pickList<CourseListItem>(data, ['courses']);
  const record = asRecord(data);
  return {
    ...(record as CatalogListData),
    courses: items,
    totalCount: Number(record.totalCount ?? items.length),
    success: record.success !== false,
  };
};

const normalizeCatalogItem = (data: unknown): CatalogItemData => {
  const item = pickOne<CourseDetail>(data, ['course', 'Course']);
  const record = asRecord(data);
  return { ...(record as CatalogItemData), course: item, success: record.success !== false };
};

const normalizeLabList = (data: unknown): LabListData => {
  const items = pickList<LabListItem>(data, ['labs']);
  const record = asRecord(data);
  return { ...(record as LabListData), labs: items, success: record.success !== false };
};

const normalizeLabItem = (data: unknown): LabItemData => {
  const item = pickOne<LabDetail>(data, ['lab']);
  const record = asRecord(data);
  return { ...(record as LabItemData), lab: item, success: record.success !== false };
};

const fetchPublicProjectList = async () => {
  const r = await api.get('/v1/projects', { params: { pageSize: 50 } });
  const projects = r.data.projects || [];
  projectCatalogCache.setList(projects);
  return projects as ProjectListItem[];
};

const fetchPublicEventList = async () => {
  const r = await api.get('/v1/events', { params: { pageSize: 50 } });
  const events = r.data.events || [];
  eventCatalogCache.setList(events);
  return events as EventListItem[];
};

const rememberEnrollmentList = async <T extends { id: number }>(
  kind: 'courses' | 'projects' | 'events' | 'gigs',
  path: string
) => {
  const enrollments = await rememberMyEnrollments<T>(kind, async () => {
    const r = await api.get<{ enrollments: T[]; success: boolean }>(path);
    return r.data.enrollments || [];
  });
  return { data: { enrollments, success: true } };
};

const refreshEnrollmentList = async <T extends { id: number }>(
  kind: 'courses' | 'projects' | 'events' | 'gigs',
  path: string
) => {
  const enrollments = await refreshMyEnrollments<T>(kind, async () => {
    const r = await api.get<{ enrollments: T[]; success: boolean }>(path);
    return r.data.enrollments || [];
  });
  return { data: { enrollments, success: true } };
};

const fetchPublicCourseList = async () => {
  const r = await api.get('/v1/courses', { params: { sort: 'catalog', pageSize: 50 } });
  const data = normalizeCatalogList(r.data);
  setCachedPublicCourseList(data.courses);
  return data.courses;
};

const canFilterPublicListLocally = (params?: Record<string, string | number | boolean | undefined>) => {
  const pageNumber = Number(params?.pageNumber ?? 1);
  return pageNumber <= 1 && params?.authorId == null;
};

export const contentApi = {
  listCourses: async (params?: Record<string, string | number | boolean | undefined>) => {
    if (canFilterPublicListLocally(params)) {
      try {
        const all = await rememberPublicCourseList(fetchPublicCourseList);
        const courses = filterPublicCourses(all, {
          search: typeof params?.search === 'string' ? params.search : undefined,
          categoryId: params?.categoryId,
          difficulty: typeof params?.difficulty === 'string' ? params.difficulty : undefined,
          sort: typeof params?.sort === 'string' ? params.sort : 'catalog',
          featuredOnly: !!params?.featuredOnly,
          minRating: params?.minRating,
        });
        return {
          data: {
            courses,
            totalCount: courses.length,
            pageNumber: 1,
            pageSize: courses.length,
            success: true,
          },
        };
      } catch {
        /* fall through to the network */
      }
    }
    const r = await api.get('/v1/courses', { params });
    const data = normalizeCatalogList(r.data);
    if (canFilterPublicListLocally(params) && !params?.search && !params?.categoryId && !params?.difficulty) {
      setCachedPublicCourseList(data.courses);
    }
    return { ...r, data };
  },
  myCourses: async (params?: Record<string, string | number | undefined>) => {
    const r = await api.get('/v1/courses/mine', { params });
    return { ...r, data: normalizeCatalogList(r.data) };
  },
  courseQueue: async (params?: Record<string, string | number | undefined>) => {
    const r = await api.get('/v1/courses/queue', { params });
    return { ...r, data: normalizeCatalogList(r.data) };
  },
  getCourse: async (id: number) => {
    const r = await api.get(`/v1/courses/${id}`);
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  getCourseBySlug: async (slug: string) => {
    const cached = getCachedCourseDetail(slug);
    if (cached) {
      return { data: { course: cached, success: true } };
    }
    const r = await api.get(`/v1/courses/by-slug/${slug}`);
    const data = normalizeCatalogItem(r.data);
    if (data.course) setCachedCourseDetail(slug, data.course);
    return { ...r, data };
  },
  getCourseTopic: (courseSlug: string, topicSlug: string, moduleId?: number) =>
    api.get<{
      topic: KnowledgeTopic;
      course: CourseListItem;
      moduleTitle?: string;
      moduleId?: number;
      moduleAuthor?: CourseListItem['author'] | null;
      assessmentId?: number;
      nextAssessmentId?: number;
      previousAssessmentId?: number;
      nextTopicSlug?: string;
      nextTopicTitle?: string;
      nextModuleTitle?: string;
      previousTopicSlug?: string;
      previousTopicTitle?: string;
      previousModuleTitle?: string;
      canLearn?: boolean;
      access?: string;
      success: boolean;
    }>(`/v1/courses/by-slug/${courseSlug}/topics/${topicSlug}`, {
      params: moduleId ? { module: moduleId } : undefined,
    }),
  saveCourse: async (payload: unknown, id?: number) => {
    const r = id
      ? await api.put(`/v1/courses/${id}`, payload, { timeout: 120000 })
      : await api.post('/v1/courses', payload, { timeout: 120000 });
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  submitCourse: async (id: number) => {
    const r = await api.post(`/v1/courses/${id}/submit`);
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  reviewCourse: async (id: number, decision: string, comments?: string) => {
    const r = await api.post(`/v1/courses/${id}/review`, { decision, comments });
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  approveCourse: async (id: number, comments?: string) => {
    const r = await api.post(`/v1/courses/${id}/approve`, { decision: 'Approved', comments });
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  publishCourse: async (id: number) => {
    const r = await api.post(`/v1/courses/${id}/publish`);
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },

  enrollCourse: async (id: number, interviewPackageCount = 0) => {
    const r = await api.post<{ enrollment?: CourseEnrollment; success: boolean; message?: string }>(
      `/v1/courses/${id}/enroll`,
      { interviewPackageCount }
    );
    if (r.data.enrollment) {
      upsertMyEnrollment('courses', r.data.enrollment);
      invalidatePublicCourseCache();
    }
    return r;
  },
  upgradeCourseInterviews: async (id: number, interviewPackageCount: number) => {
    const r = await api.post<{ enrollment?: CourseEnrollment; success: boolean; message?: string }>(
      `/v1/courses/${id}/enrollment/upgrade-interviews`,
      { interviewPackageCount }
    );
    if (r.data.enrollment) upsertMyEnrollment('courses', r.data.enrollment);
    return r;
  },
  getCourseInterviewPackages: () =>
    api.get<CourseInterviewPackageConfig>('/v1/courses/interview-packages'),
  registerCourseInterest: (id: number) =>
    api.post<{ success: boolean; message?: string }>(`/v1/courses/${id}/interest`),
  listCourseEnrollments: (id: number) =>
    api.get<{ enrollments: CourseEnrollment[]; success: boolean; message?: string }>(`/v1/courses/${id}/enrollments`),
  listCourseInterests: (id: number) =>
    api.get<{ interests: Array<{ id: number; userProfileId: number; userEmail: string; status: string; dateCreated: string }>; success: boolean; message?: string }>(`/v1/courses/${id}/interests`),
  enrollFromInterest: (courseId: number, interestId: number) =>
    api.post<{ enrollment?: CourseEnrollment; success: boolean; message?: string }>(`/v1/courses/${courseId}/interests/${interestId}/enroll`),
  getCatalogSettings: () =>
    api.get<{ settings?: CatalogSettings; success: boolean; message?: string }>('/v1/catalog-settings'),
  updateCatalogSettings: (payload: Partial<CatalogSettings>) =>
    api.put<{ settings?: CatalogSettings; success: boolean; message?: string }>('/v1/catalog-settings', payload),
  getCourseEnrollment: (id: number) =>
    api.get<{ enrollment?: CourseEnrollment | null; success: boolean; message?: string }>(`/v1/courses/${id}/enrollment`),
  myCourseEnrollments: () => rememberEnrollmentList<CourseEnrollment>('courses', '/v1/courses/enrollments/mine'),
  refreshCourseEnrollments: () => refreshEnrollmentList<CourseEnrollment>('courses', '/v1/courses/enrollments/mine'),
  completeCoursePayment: async (enrollmentId: number) => {
    const r = await api.post<{ enrollment?: CourseEnrollment; success: boolean; message?: string }>(
      `/v1/courses/enrollments/${enrollmentId}/complete-payment`
    );
    if (r.data.enrollment) {
      upsertMyEnrollment('courses', r.data.enrollment);
      invalidatePublicCourseCache();
    }
    return r;
  },
  redeemCourseCode: async (payload: { email?: string; code: string; courseId?: number }) => {
    const r = await api.post<{ enrollment?: CourseEnrollment; success: boolean; message?: string }>('/v1/courses/redeem-code', payload);
    if (r.data.enrollment) {
      upsertMyEnrollment('courses', r.data.enrollment);
      invalidatePublicCourseCache();
    }
    return r;
  },
  getCourseProgress: (courseId: number) =>
    api.get<{ progress?: CourseProgress; success: boolean; message?: string }>(`/v1/courses/${courseId}/progress`),
  completeTopic: (courseId: number, topicId: number) =>
    api.post<{ progress?: CourseProgress; success: boolean; message?: string }>(`/v1/courses/${courseId}/progress/topics/${topicId}/complete`),
  completeModule: (courseId: number, moduleId: number) =>
    api.post<{ progress?: CourseProgress; success: boolean; message?: string }>(`/v1/courses/${courseId}/progress/modules/${moduleId}/complete`),
  completeCourseProgress: (courseId: number) =>
    api.post<{ progress?: CourseProgress; success: boolean; message?: string }>(`/v1/courses/${courseId}/progress/complete`),
  completeLab: (courseId: number, labId: number) =>
    api.post<{ progress?: CourseProgress; success: boolean; message?: string }>(`/v1/courses/${courseId}/progress/labs/${labId}/complete`),
  archiveCourse: async (id: number) => {
    const r = await api.delete(`/v1/courses/${id}`);
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  permanentDeleteCourse: async (id: number) => {
    const r = await api.delete(`/v1/courses/${id}/permanent`);
    invalidatePublicCourseCache();
    return { ...r, data: normalizeCatalogItem(r.data) };
  },
  archiveLab: async (id: number) => {
    const r = await api.delete(`/v1/labs/${id}`);
    return { ...r, data: normalizeLabItem(r.data) };
  },
  permanentDeleteLab: async (id: number) => {
    const r = await api.delete(`/v1/labs/${id}/permanent`);
    return { ...r, data: normalizeLabItem(r.data) };
  },
  archiveProject: async (id: number) => {
    const r = await api.delete<{ success: boolean }>(`/v1/projects/${id}`);
    projectCatalogCache.invalidate();
    return r;
  },
  permanentDeleteProject: async (id: number) => {
    const r = await api.delete<{ success: boolean }>(`/v1/projects/${id}/permanent`);
    projectCatalogCache.invalidate();
    return r;
  },
  listCourseInvites: (courseId: number) =>
    api.get<{ invites: CourseInvite[]; success: boolean }>(`/v1/admin/courses/${courseId}/invites`),
  createCourseInvite: (courseId: number, email: string) =>
    api.post<{ invite?: CourseInvite; success: boolean; message?: string }>(`/v1/admin/courses/${courseId}/invites`, { email }),
  inviteCourseEmails: (courseId: number, emails: string[], issueAccessCodes = true) =>
    api.post<{
      success: boolean;
      message?: string;
      created?: number;
      invites?: CourseInvite[];
      skipped?: string[];
    }>(`/v1/courses/${courseId}/invites`, { emails, issueAccessCodes }),
  rateCourse: async (courseId: number, rating: number, comment?: string) => {
    const r = await api.post<{ success: boolean; message?: string; averageRating: number; ratingCount: number }>(`/v1/courses/${courseId}/rate`, { rating, comment });
    invalidatePublicCourseCache();
    return r;
  },
  rateLab: async (labId: number, rating: number, comment?: string) => {
    return api.post<{ success: boolean; message?: string; averageRating: number; ratingCount: number }>(`/v1/labs/${labId}/rate`, { rating, comment });
  },
  courseEarnings: () =>
    api.get<{
      success: boolean;
      collected: number;
      courses: { id: number; title: string; slug: string; status: string; isPublic: boolean; averageRating: number; ratingCount: number; enrollments: number; revenue: number }[];
    }>('/v1/courses/earnings'),
  courseChangelog: (courseId: number) =>
    api.get<{ success: boolean; entries: { action: string; summary?: string | null; changedBy?: string | null; changedByRole?: string | null; dateCreated: string }[] }>(`/v1/courses/${courseId}/changelog`),
  listCourseAdmins: (courseId: number) =>
    api.get<{ grants: CourseAdminGrant[]; success: boolean }>(`/v1/admin/courses/${courseId}/admins`),
  saveCourseAdmin: (courseId: number, payload: Partial<CourseAdminGrant> & { userProfileId: number }) =>
    api.post<{ grants: CourseAdminGrant[]; success: boolean }>(`/v1/admin/courses/${courseId}/admins`, payload),
  removeCourseAdmin: (courseId: number, grantId: number) =>
    api.delete<{ grants: CourseAdminGrant[]; success: boolean }>(`/v1/admin/courses/${courseId}/admins/${grantId}`),
  enrollmentReport: () =>
    api.get<{ rows: EnrollmentReportRow[]; success: boolean }>('/v1/admin/reports/enrollments'),
  projectEnrollmentReport: () =>
    api.get<{ rows: ProjectEnrollmentReportRow[]; success: boolean }>('/v1/admin/reports/project-enrollments'),
  cancelCourseEnrollment: (enrollmentId: number) =>
    api.post<{ enrollment?: CourseEnrollment; success: boolean; message?: string }>(
      `/v1/courses/enrollments/${enrollmentId}/cancel`
    ),
  cancelProjectEnrollment: (enrollmentId: number) =>
    api.post<{ success: boolean; message?: string }>(
      `/v1/projects/enrollments/${enrollmentId}/cancel`
    ),

  listLabs: async (params?: Record<string, string | number | boolean | undefined>) => {
    const r = await api.get('/v1/labs', { params });
    return { ...r, data: normalizeLabList(r.data) };
  },
  myLabs: async (params?: Record<string, string | number | boolean | undefined>) => {
    const r = await api.get('/v1/labs/mine', { params });
    return { ...r, data: normalizeLabList(r.data) };
  },
  labQueue: async (params?: Record<string, string | number | undefined>) => {
    const r = await api.get('/v1/labs/queue', { params });
    return { ...r, data: normalizeLabList(r.data) };
  },
  getLab: async (id: number) => {
    const r = await api.get(`/v1/labs/${id}`);
    return { ...r, data: normalizeLabItem(r.data) };
  },
  getLabBySlug: async (slug: string, courseSlug?: string | null) => {
    const r = await api.get(`/v1/labs/by-slug/${slug}`, { params: courseSlug ? { course: courseSlug } : undefined });
    return { ...r, data: normalizeLabItem(r.data) };
  },
  getLabTopic: (labSlug: string, topicSlug: string, courseSlug?: string | null) =>
    api.get<{
      topic: LabTopic;
      lab: LabListItem;
      moduleTitle?: string;
      nextTopicSlug?: string;
      nextTopicTitle?: string;
      nextModuleTitle?: string;
      previousTopicSlug?: string;
      previousTopicTitle?: string;
      previousModuleTitle?: string;
      success: boolean;
    }>(`/v1/labs/by-slug/${labSlug}/topics/${topicSlug}`, { params: courseSlug ? { course: courseSlug } : undefined }),
  saveLab: async (payload: unknown, id?: number) => {
    const r = id ? await api.put(`/v1/labs/${id}`, payload) : await api.post('/v1/labs', payload);
    return { ...r, data: normalizeLabItem(r.data) };
  },
  submitLab: async (id: number) => {
    const r = await api.post(`/v1/labs/${id}/submit`);
    return { ...r, data: normalizeLabItem(r.data) };
  },
  reviewLab: async (id: number, decision: string, comments?: string) => {
    const r = await api.post(`/v1/labs/${id}/review`, { decision, comments });
    return { ...r, data: normalizeLabItem(r.data) };
  },
  approveLab: async (id: number, comments?: string) => {
    const r = await api.post(`/v1/labs/${id}/approve`, { decision: 'Approved', comments });
    return { ...r, data: normalizeLabItem(r.data) };
  },
  publishLab: async (id: number) => {
    const r = await api.post(`/v1/labs/${id}/publish`);
    return { ...r, data: normalizeLabItem(r.data) };
  },

  listProjects: async (params?: Record<string, string | number | boolean | undefined>) => {
    const pageNumber = Number(params?.pageNumber ?? 1);
    const hasSearch = typeof params?.search === 'string' && params.search.trim().length > 0;
    if (pageNumber <= 1 && !hasSearch) {
      try {
        const all = await projectCatalogCache.rememberList(fetchPublicProjectList);
        const projects = filterPublicProjects(all, {
          search: undefined,
          categoryId: params?.categoryId,
          difficulty: typeof params?.difficulty === 'string' ? params.difficulty : undefined,
          engagementType: typeof params?.engagementType === 'string' ? params.engagementType : undefined,
        });
        return {
          data: {
            projects,
            totalCount: projects.length,
            success: true,
          },
        };
      } catch {
        /* fall through */
      }
    }
    const r = await api.get<{ projects: ProjectListItem[]; totalCount: number; success: boolean }>('/v1/projects', { params });
    if (pageNumber <= 1 && !params?.search && !params?.categoryId && !params?.difficulty && !params?.engagementType) {
      projectCatalogCache.setList(r.data.projects || []);
    }
    return r;
  },
  myProjects: () => api.get<{ projects: ProjectListItem[]; success: boolean }>('/v1/projects/mine'),
  projectQueue: (params?: Record<string, string | number | undefined>) =>
    api.get<{ projects: ProjectListItem[]; success: boolean }>('/v1/projects/queue', { params }),
  getProject: (id: number) => api.get<{ project: ProjectDetail; success: boolean }>(`/v1/projects/${id}`),
  getProjectBySlug: async (slug: string) => {
    const overlay = async (project: ProjectDetail): Promise<ProjectDetail> => {
      try {
        const mine = await rememberMyEnrollments<ProjectEnrollment>('projects', async () => {
          const r = await api.get<{ enrollments: ProjectEnrollment[]; success: boolean }>('/v1/projects/enrollments/mine');
          return r.data.enrollments || [];
        });
        const enrollment = mine.find((item) => item.projectId === project.id || item.projectSlug === slug);
        return { ...project, enrollment: enrollment ?? project.enrollment };
      } catch {
        return project;
      }
    };
    const cached = !localStorage.getItem('token') ? projectCatalogCache.getDetail(slug) : undefined;
    if (cached) {
      return { data: { project: await overlay({ ...cached, enrollment: undefined, mentorRequest: undefined }), success: true } };
    }
    const r = await api.get<{ project: ProjectDetail; success: boolean }>(`/v1/projects/by-slug/${slug}`);
    if (r.data.project) {
      projectCatalogCache.setDetail(slug, { ...r.data.project, enrollment: undefined, mentorRequest: undefined });
      return { ...r, data: { ...r.data, project: await overlay(r.data.project) } };
    }
    return r;
  },
  saveProject: async (payload: unknown, id?: number) => {
    const r = id
      ? await api.put<{ project: ProjectDetail; success: boolean }>(`/v1/projects/${id}`, payload)
      : await api.post<{ project: ProjectDetail; success: boolean }>('/v1/projects', payload);
    projectCatalogCache.invalidate();
    return r;
  },
  submitProject: async (id: number) => {
    const r = await api.post<{ project: ProjectDetail; success: boolean }>(`/v1/projects/${id}/submit`);
    projectCatalogCache.invalidate();
    return r;
  },
  reviewProject: async (id: number, decision: string, comments?: string) => {
    const r = await api.post<{ project: ProjectDetail; success: boolean }>(`/v1/projects/${id}/review`, { decision, comments });
    projectCatalogCache.invalidate();
    return r;
  },
  approveProject: async (id: number, comments?: string) => {
    const r = await api.post<{ project: ProjectDetail; success: boolean }>(`/v1/projects/${id}/approve`, { decision: 'Approved', comments });
    projectCatalogCache.invalidate();
    return r;
  },
  publishProject: async (id: number) => {
    const r = await api.post<{ project: ProjectDetail; success: boolean }>(`/v1/projects/${id}/publish`);
    projectCatalogCache.invalidate();
    return r;
  },

  listGigs: (params?: Record<string, string | number | boolean | undefined>) =>
    api.get<{ gigs: GigListItem[]; totalCount: number; success: boolean }>('/v1/gigs', { params }),
  myGigs: () => api.get<{ gigs: GigListItem[]; success: boolean }>('/v1/gigs/mine'),
  gigQueue: (params?: Record<string, string | number | undefined>) =>
    api.get<{ gigs: GigListItem[]; success: boolean }>('/v1/gigs/queue', { params }),
  getGig: (id: number) => api.get<{ gig: GigDetail; success: boolean }>(`/v1/gigs/${id}`),
  getGigBySlug: (slug: string) => api.get<{ gig: GigDetail; success: boolean }>(`/v1/gigs/by-slug/${slug}`),
  saveGig: (payload: unknown, id?: number) =>
    id ? api.put<{ gig: GigDetail; success: boolean }>(`/v1/gigs/${id}`, payload)
      : api.post<{ gig: GigDetail; success: boolean }>('/v1/gigs', payload),
  submitGig: (id: number) => api.post<{ gig: GigDetail; success: boolean }>(`/v1/gigs/${id}/submit`),
  reviewGig: (id: number, decision: string, comments?: string) =>
    api.post<{ gig: GigDetail; success: boolean }>(`/v1/gigs/${id}/review`, { decision, comments }),
  approveGig: (id: number, comments?: string) =>
    api.post<{ gig: GigDetail; success: boolean }>(`/v1/gigs/${id}/approve`, { decision: 'Approved', comments }),
  publishGig: (id: number) => api.post<{ gig: GigDetail; success: boolean }>(`/v1/gigs/${id}/publish`),
  archiveGig: (id: number) => api.delete<{ success: boolean }>(`/v1/gigs/${id}`),
  permanentDeleteGig: (id: number) => api.delete<{ success: boolean }>(`/v1/gigs/${id}/permanent`),
  applyGig: async (id: number) => {
    const r = await api.post<{ enrollment: GigEnrollment; success: boolean; message?: string }>(`/v1/gigs/${id}/apply`);
    if (r.data.enrollment) upsertMyEnrollment('gigs', r.data.enrollment);
    return r;
  },
  myGigEnrollments: () => rememberEnrollmentList<GigEnrollment>('gigs', '/v1/gigs/enrollments/mine'),
  refreshGigEnrollments: () => refreshEnrollmentList<GigEnrollment>('gigs', '/v1/gigs/enrollments/mine'),
  gigEnrollments: (params?: Record<string, string | number | undefined>) =>
    api.get<{ enrollments: GigEnrollment[]; success: boolean }>('/v1/gigs/enrollments', { params }),
  decideGigEnrollment: async (enrollmentId: number, decision: string, comments?: string) => {
    const r = await api.post<{ enrollment: GigEnrollment; success: boolean }>(`/v1/gigs/enrollments/${enrollmentId}/decide`, { decision, comments });
    if (r.data.enrollment) upsertMyEnrollment('gigs', r.data.enrollment);
    return r;
  },

  listEvents: async (params?: Record<string, string | number | boolean | undefined>) => {
    const pageNumber = Number(params?.pageNumber ?? 1);
    if (pageNumber <= 1) {
      try {
        const all = await eventCatalogCache.rememberList(fetchPublicEventList);
        const events = filterPublicEvents(all, {
          search: typeof params?.search === 'string' ? params.search : undefined,
          categoryId: params?.categoryId,
        });
        const limited = typeof params?.pageSize === 'number' ? events.slice(0, params.pageSize) : events;
        return {
          data: {
            events: limited,
            totalCount: events.length,
            success: true,
          },
        };
      } catch {
        /* fall through */
      }
    }
    const r = await api.get<{ events: EventListItem[]; totalCount: number; success: boolean }>('/v1/events', { params });
    if (pageNumber <= 1 && !params?.search && !params?.categoryId) {
      eventCatalogCache.setList(r.data.events || []);
    }
    return r;
  },
  myEvents: () => api.get<{ events: EventListItem[]; success: boolean }>('/v1/events/mine'),
  eventQueue: () => api.get<{ events: EventListItem[]; success: boolean }>('/v1/events/queue'),
  getEvent: (id: number) => api.get<{ event: EventDetail; success: boolean }>(`/v1/events/${id}`),
  getEventBySlug: async (slug: string) => {
    const overlay = async (event: EventDetail): Promise<EventDetail> => {
      try {
        const mine = await rememberMyEnrollments<EventEnrollment>('events', async () => {
          const r = await api.get<{ enrollments: EventEnrollment[]; success: boolean }>('/v1/events/enrollments/mine');
          return r.data.enrollments || [];
        });
        const enrollment = mine.find((item) => item.eventId === event.id || item.eventSlug === slug);
        return {
          ...event,
          enrollment: enrollment ?? event.enrollment,
          meetingLink: enrollment?.meetingLink ?? event.meetingLink,
          canSeeMeetingLink: Boolean(enrollment?.meetingLink) || event.canSeeMeetingLink,
        };
      } catch {
        return event;
      }
    };
    const cached = eventCatalogCache.getDetail(slug);
    if (cached) {
      return { data: { event: await overlay({ ...cached, enrollment: undefined, canSeeMeetingLink: false, meetingLink: undefined }), success: true } };
    }
    const r = await api.get<{ event: EventDetail; success: boolean }>(`/v1/events/by-slug/${slug}`);
    if (r.data.event) {
      eventCatalogCache.setDetail(slug, { ...r.data.event, enrollment: undefined, canSeeMeetingLink: false, meetingLink: undefined });
      return { ...r, data: { ...r.data, event: await overlay(r.data.event) } };
    }
    return r;
  },
  saveEvent: async (payload: unknown, id?: number) => {
    const r = id
      ? await api.put<{ event: EventDetail; success: boolean }>(`/v1/events/${id}`, payload)
      : await api.post<{ event: EventDetail; success: boolean }>('/v1/events', payload);
    eventCatalogCache.invalidate();
    return r;
  },
  submitEvent: async (id: number) => {
    const r = await api.post<{ event: EventDetail; success: boolean }>(`/v1/events/${id}/submit`);
    eventCatalogCache.invalidate();
    return r;
  },
  reviewEvent: async (id: number, decision: string, comments?: string) => {
    const r = await api.post<{ event: EventDetail; success: boolean }>(`/v1/events/${id}/review`, { decision, comments });
    eventCatalogCache.invalidate();
    return r;
  },
  approveEvent: async (id: number, comments?: string) => {
    const r = await api.post<{ event: EventDetail; success: boolean }>(`/v1/events/${id}/approve`, { decision: 'Approved', comments });
    eventCatalogCache.invalidate();
    return r;
  },
  publishEvent: async (id: number) => {
    const r = await api.post<{ event: EventDetail; success: boolean }>(`/v1/events/${id}/publish`);
    eventCatalogCache.invalidate();
    return r;
  },
  archiveEvent: async (id: number) => {
    const r = await api.delete<{ success: boolean }>(`/v1/events/${id}`);
    eventCatalogCache.invalidate();
    return r;
  },
  permanentDeleteEvent: async (id: number) => {
    const r = await api.delete<{ success: boolean }>(`/v1/events/${id}/permanent`);
    eventCatalogCache.invalidate();
    return r;
  },
  enrollEvent: async (id: number) => {
    const r = await api.post<{ enrollment: EventEnrollment; success: boolean; message?: string }>(`/v1/events/${id}/enroll`);
    if (r.data.enrollment) upsertMyEnrollment('events', r.data.enrollment);
    return r;
  },
  myEventEnrollments: () => rememberEnrollmentList<EventEnrollment>('events', '/v1/events/enrollments/mine'),
  refreshEventEnrollments: () => refreshEnrollmentList<EventEnrollment>('events', '/v1/events/enrollments/mine'),
  eventEnrollments: (params?: Record<string, string | number | undefined>) =>
    api.get<{ enrollments: EventEnrollment[]; success: boolean }>('/v1/events/enrollments', { params }),
  decideEventEnrollment: async (enrollmentId: number, decision: string, comments?: string) => {
    const r = await api.post<{ enrollment: EventEnrollment; success: boolean }>(`/v1/events/enrollments/${enrollmentId}/decide`, { decision, comments });
    if (r.data.enrollment) upsertMyEnrollment('events', r.data.enrollment);
    return r;
  },
  payEventEnrollment: async (enrollmentId: number) => {
    const r = await api.post<{ enrollment: EventEnrollment; success: boolean }>(`/v1/events/enrollments/${enrollmentId}/pay`);
    if (r.data.enrollment) upsertMyEnrollment('events', r.data.enrollment);
    return r;
  },

  enrollProject: async (id: number) => {
    const r = await api.post<{ enrollment: ProjectEnrollment; success: boolean; message?: string }>(`/v1/projects/${id}/enroll`);
    if (r.data.enrollment) upsertMyEnrollment('projects', r.data.enrollment);
    return r;
  },
  submitProjectWork: async (id: number, payload: { gitRepoUrl: string; architectureDocUrl?: string; setupInstructionsUrl?: string }) => {
    const r = await api.post<{ enrollment: ProjectEnrollment; success: boolean; message?: string }>(`/v1/projects/${id}/submit-work`, payload);
    if (r.data.enrollment) upsertMyEnrollment('projects', r.data.enrollment);
    return r;
  },
  myProjectEnrollments: () => rememberEnrollmentList<ProjectEnrollment>('projects', '/v1/projects/enrollments/mine'),
  refreshProjectEnrollments: () => refreshEnrollmentList<ProjectEnrollment>('projects', '/v1/projects/enrollments/mine'),
  requestProjectMentorSupport: (id: number, payload: { rolePreference: string; notes?: string }) =>
    api.post<{ request?: ProjectMentorRequest; success: boolean; message?: string }>(`/v1/projects/${id}/mentor-requests`, payload),
  listProjectMentorRequests: (params?: { projectId?: number; status?: string }) =>
    api.get<{ requests: ProjectMentorRequest[]; success: boolean; message?: string }>('/v1/projects/mentor-requests', { params }),
  closeProjectMentorRequest: (requestId: number, adminNotes?: string) =>
    api.post<{ request?: ProjectMentorRequest; success: boolean; message?: string }>(
      `/v1/projects/mentor-requests/${requestId}/close`,
      { adminNotes }
    ),

  getNav: () =>
    api.get<{ items: NavMenuItem[]; hasPublishedGigs: boolean; hasPublishedEvents: boolean; hasPublishedLabs: boolean; success: boolean }>('/v1/nav', {
      skipAuth: true,
    }),
  saveNav: (items: NavMenuItem[]) => api.put<{ items: NavMenuItem[]; success: boolean }>('/v1/nav', { items }),

  listCategories: (kind?: string) => api.get<{ categories: Category[]; success: boolean }>('/v1/categories', { params: { kind } }),
  courseMenu: () => api.get<{ categories: CategoryMenu[]; success: boolean }>('/v1/categories/menu'),
  labMenu: () => api.get<{ categories: CategoryMenu[]; success: boolean }>('/v1/categories/lab-menu'),
  listModules: () => api.get<{ modules: KnowledgeModule[]; success: boolean }>('/v1/modules'),
  getModule: (id: number) => api.get<{ module: KnowledgeModule; success: boolean }>(`/v1/modules/${id}`),
  saveModule: (payload: unknown, id?: number, courseId?: number) =>
    id
      ? api.put<{ module: KnowledgeModule; success: boolean }>(`/v1/modules/${id}`, payload, { params: { courseId } })
      : api.post<{ module: KnowledgeModule; success: boolean }>('/v1/modules', payload, { params: { courseId } }),
  setModuleActive: (id: number, isActive: boolean) =>
    api.post<{ success: boolean; message?: string }>(`/v1/modules/${id}/active`, { isActive }),
  deleteModule: (id: number) =>
    api.delete<{ success: boolean; message?: string }>(`/v1/modules/${id}`),
  catalogTree: () =>
    api.get<{ categories: CatalogTreeCategory[]; unlinkedLabs: CatalogTreeLab[]; success: boolean }>('/v1/categories/tree'),
  setCourseActive: async (id: number, isActive: boolean) => {
    const r = await api.post<{ success: boolean; message?: string }>(`/v1/courses/${id}/active`, { isActive });
    invalidatePublicCourseCache();
    return r;
  },
  setLabActive: (id: number, isActive: boolean) =>
    api.post<{ success: boolean; message?: string }>(`/v1/labs/${id}/active`, { isActive }),
  getAssessment: (courseId: number, assessmentId: number) =>
    api.get<{ assessment?: ModuleAssessment; progress?: CourseProgress; success: boolean; message?: string }>(
      `/v1/courses/${courseId}/assessments/${assessmentId}`
    ),
  submitAssessment: (courseId: number, assessmentId: number, answers: { questionId: number; optionId: number }[]) =>
    api.post<{ assessment?: ModuleAssessment; progress?: CourseProgress; success: boolean; message?: string }>(
      `/v1/courses/${courseId}/assessments/${assessmentId}/submit`,
      { answers }
    ),
  saveCategory: async (payload: unknown, id?: number) => {
    const r = id ? await api.put(`/v1/categories/${id}`, payload) : await api.post('/v1/categories', payload);
    invalidatePublicCourseCache();
    return r;
  },
  deleteCategory: async (id: number) => {
    const r = await api.delete(`/v1/categories/${id}`);
    invalidatePublicCourseCache();
    return r;
  },
  reorderCategories: async (items: { id: number; displayOrder: number }[]) => {
    const r = await api.put('/v1/categories/reorder', { items });
    invalidatePublicCourseCache();
    return r;
  },
  listTags: () => api.get<{ tags: { id: number; name: string; slug: string }[] }>('/v1/tags'),
  listAuthors: () => api.get<{ authors: AuthorCard[]; success: boolean }>('/v1/authors'),
  dashboard: () => api.get<{ dashboard: ContentDashboard; success: boolean }>('/v1/dashboard'),
  generate: (payload: unknown) => api.post<{ content: GeneratedContent; coursePlan?: GeneratedCoursePlan; success: boolean; message?: string }>('/v1/ai/generate-content', payload, { timeout: 240000 }),
  generationTypes: () => api.get<{ types: { contentType: string; schemaVersion: string; promptTemplateVersion: string }[]; success: boolean }>('/v1/content-generation/types'),
  createGeneration: (payload: { prompt: string; contentType: string; schemaVersion: string }, idempotencyKey: string) =>
    api.post<{ jobId: string; status: string; statusUrl: string; success: boolean; message?: string }>('/v1/content-generation', payload, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  generationJobs: (params: { contentType?: string; status?: string; page?: number; pageSize?: number }) =>
    api.get<{ jobs: { jobId: string; status: string; contentType: string; promptPreview?: string }[]; totalCount: number; success: boolean }>('/v1/content-generation', { params }),
  generationJob: (id: string) => api.get<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}`),
  coursePlan: (id: string) => api.post<{ coursePlan?: GeneratedCoursePlan; success: boolean; message?: string }>(`/v1/content-generation/${id}/course-plan`, null, { timeout: 600000 }),
  generationContent: (id: string) => api.get(`/v1/content-generation/${id}/content`),
  downloadGeneration: (id: string) => api.get(`/v1/content-generation/${id}/file`, { responseType: 'blob' }),
  importGeneration: (id: string) => api.post<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}/import`, null, { timeout: 600000 }),
  reimportGeneration: (id: string) => api.post<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}/reimport`, null, { timeout: 600000 }),
  cancelGeneration: (id: string) => api.post<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}/cancel`),
  deleteGeneration: (id: string) => api.delete<{ success: boolean; message?: string }>(`/v1/content-generation/${id}`, { timeout: 600000 }),
  retryGeneration: (id: string) => api.post<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}/retry`),
  rejectGeneration: (id: string) => api.post<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}/reject`),
  deleteGenerationFile: (id: string) => api.delete<{ job: any; success: boolean; message?: string }>(`/v1/content-generation/${id}/file`),
  llmProviders: () => api.get('/v1/ai/providers'),
  saveProvider: (payload: unknown, id?: number) =>
    id ? api.put(`/v1/ai/providers/${id}`, payload) : api.post('/v1/ai/providers', payload),
  deleteProvider: (id: number) => api.delete(`/v1/ai/providers/${id}`),
  saveModel: (payload: unknown, id?: number) =>
    id ? api.put(`/v1/ai/models/${id}`, payload) : api.post('/v1/ai/models', payload),
  deleteModel: (id: number) => api.delete(`/v1/ai/models/${id}`),

  uploadMedia: async (file: File, altText?: string, caption?: string, context?: {
    projectId?: number;
    courseId?: number;
    labId?: number;
    catalogKind?: string;
    entityName?: string;
    topicName?: string;
  }) => {
    const form = new FormData();
    form.append('file', file);
    if (altText) form.append('altText', altText);
    if (caption) form.append('caption', caption);
    if (context?.projectId) form.append('projectId', String(context.projectId));
    if (context?.courseId) form.append('courseId', String(context.courseId));
    if (context?.labId) form.append('labId', String(context.labId));
    if (context?.catalogKind) form.append('catalogKind', context.catalogKind);
    if (context?.entityName) form.append('entityName', context.entityName);
    if (context?.topicName) form.append('topicName', context.topicName);
    return api.post<{ media: { id: number; url: string; contentType?: string; fileName?: string }; success: boolean }>('/v1/media', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export type { Envelope };
